import './TableSection.scss';

import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { Media, TableBlock } from '@travelwin/cms/types';
import { Body, Title } from '@travelwin/core';
import classNames from 'classnames';
import { ReactNode } from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import { RichText } from '@/components/RichText';
import { serializeLexicalChildNode } from '@/components/RichText/serialize-lexical';
import { isRichTextEmpty } from '@/utils/lexicalUtilts';

const className = 'c-TableSection';

export const TableSection = ({ data }: { data: TableBlock }) => {
  const { columns, maxWidth } = data;
  const rowCount = Math.max(...columns.map((column) => column.cells.length));

  return (
    <div className={className}>
      {!isRichTextEmpty(data.title) && (
        <Title tag={2} level={{ sm: 3, md: 2, lg: 2 }} className="text-center">
          {serializeLexicalChildNode({ content: data.title })}
        </Title>
      )}
      {!isRichTextEmpty(data.description) && (
        <div className="text-center">
          <RichText content={data.description} />
        </div>
      )}
      <div
        className={`${className}__table-container`}
        style={{ maxWidth: maxWidth ?? undefined }}
      >
        <table className={`${className}__table`}>
          <tbody>
            {Array.from({ length: rowCount }, (_, rowIndex) => {
              return (
                <tr key={rowIndex}>
                  {columns.map((column) => {
                    const highlighted = column.highlighted ?? false;
                    const cell = column.cells.at(rowIndex);

                    if (!cell) {
                      return null;
                    }

                    switch (cell.type) {
                      case 'text':
                        return (
                          <TextCell
                            key={cell.id}
                            highlighted={highlighted}
                            align={cell.align}
                          >
                            {serializeLexicalChildNode({
                              content: cell.text,
                            })}
                          </TextCell>
                        );
                      case 'checkbox':
                        return (
                          <CheckCell
                            key={cell.id}
                            value={cell.checkbox ?? false}
                            highlighted={highlighted}
                          />
                        );
                      case 'media':
                        return (
                          <MediaCell
                            key={cell.id}
                            media={cell.media}
                            highlighted={highlighted}
                          />
                        );
                      default:
                        return null;
                    }
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TextCell = ({
  children,
  highlighted,
  align,
}: {
  children: ReactNode;
  highlighted?: boolean;
  align?: 'left' | 'center' | 'right' | null;
}) => {
  return (
    <td
      className={classNames(`${className}__cell`, `${className}__cell--text`, {
        [`${className}__cell--highlighted`]: highlighted,
        [`${className}__cell--align-${align}`]: align,
      })}
    >
      <Body
        accent="title"
        size={null}
        weight={highlighted ? '600' : undefined}
        className={classNames({
          [`${className}__text--highlighted`]: highlighted,
        })}
      >
        {children}
      </Body>
    </td>
  );
};

const CheckCell = ({
  value,
  highlighted,
}: {
  value: boolean;
  highlighted?: boolean;
}) => {
  return (
    <td
      className={classNames(`${className}__cell`, `${className}__cell--check`, {
        [`${className}__cell--highlighted`]: highlighted,
      })}
    >
      {value ? (
        <CheckCircle
          weight="fill"
          size={24}
          className={classNames(
            `${className}__icon`,
            `${className}__icon--check`,
            {
              [`${className}__icon--highlighted`]: highlighted,
            },
          )}
        />
      ) : (
        <XCircle
          weight="fill"
          size={24}
          className={classNames(`${className}__icon`, `${className}__icon--x`, {
            [`${className}__icon--highlighted`]: highlighted,
          })}
        />
      )}
    </td>
  );
};

const MediaCell = ({
  media,
  highlighted,
}: {
  media: number | Media | null | undefined;
  highlighted?: boolean;
}) => {
  return (
    <td
      className={classNames(`${className}__cell`, `${className}__cell--image`, {
        [`${className}__cell--highlighted`]: highlighted,
      })}
    >
      <ImageMedia
        media={media}
        className={`${className}__image`}
        sizes="(min-width: 768px) 64px, 32px"
      />
    </td>
  );
};
